import { useEditCourseGoBack } from "@/hooks/ui/useEditCourseRoute";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { courseByIdQueryOptions, useEditCourse } from "@vixi/api";
import { useTranslation } from "@vixi/localization";
import { RouteOptionsById, router, RouterIds } from "@vixi/router";
import { Course } from "@vixi/types";
import {
  Button,
  CourseEditor,
  CourseEditorContextProvider,
  LoadingScreen,
  promiseToast,
} from "@vixi/ui";
import { assert, courseToSavedCourse } from "@vixi/utils";
import { useCallback, useEffect, useState } from "react";

const routeId = "/_app/courses/$courseId/edit-lessons" satisfies RouterIds;
const Route = router.routesById[routeId];

export const EditLessonsPageOptions: RouteOptionsById<"/_app/courses/$courseId/edit-lessons"> = {
  component: RouteComponent,
  loader: async ({ context: { queryClient }, params: { courseId } }) => {
    queryClient.ensureQueryData(courseByIdQueryOptions(queryClient, courseId));
  },
};

function RouteComponent() {
  useEditCourseGoBack();
  const { t } = useTranslation();
  const navigate = Route.useNavigate();

  const goBack = useCallback(() => {
    navigate({
      to: "/courses/$courseId",
      params: { courseId: courseId || "" },
    });
  }, []);
  const queryClient = useQueryClient();

  const { courseId } = Route.useParams();
  const [courseData, setCourseData] = useState<null | Course | undefined>(null);

  const { data: fetchedCourse, isFetching } = useQuery(
    courseByIdQueryOptions(queryClient, courseId),
  );
  useEffect(() => {
    if (fetchedCourse) {
      setCourseData(fetchedCourse);
    }
  }, [fetchedCourse]);

  const { mutateAsync: editCourse, isPending: isSavingChanges } = useEditCourse();
  const saveChanges = async () => {
    assert(!!courseData, "Course MUST be true");

    const promise = editCourse(
      { courseId: parseInt(courseId), course: courseToSavedCourse(courseData) },
      {
        onSuccess: (res) => {
          if (res) {
            goBack();
          }
        },
      },
    );

    promiseToast({
      promise,
      loadingContent: {
        title: t("LoadingMessage.SavingChanges"),
        description: t("LoadingMessage.Mins2"),
      },
      successContent: {
        title: t("SuccessMessage.ChangesSaved"),
        description: t("LoadingMessage.Mins2"),
      },
    });
  };

  if (isFetching) return <LoadingScreen />;

  return (
    <CourseEditorContextProvider setCourseData={setCourseData} courseData={courseData}>
      <CourseEditor
        navGroup={
          <div className="self-end">
            <Button
              size="small"
              variant="outlined"
              disabled={isSavingChanges}
              onClick={saveChanges}
            >
              {t("Action.SaveChanges")}
            </Button>
          </div>
        }
      />
    </CourseEditorContextProvider>
  );
}
